import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { TooltipContentProps } from '@radix-ui/react-tooltip'
import { cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

const TooltipProvider = TooltipPrimitive.Provider

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Trigger
    ref={ref}
    className={cn('flex', className)}
    data-testid='tooltip-trigger'
    {...props}
  />
))
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName

const tooltipContentVariants = cva(
  cn(
    'shadow-tooltip z-max overflow-hidden rounded p-2',
    'animate-in fade-in-0 zoom-in-95',
    '!text-body-small',
    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
    'data-[side=bottom]:slide-in-from-top-2',
    'data-[side=left]:slide-in-from-right-2',
    'data-[side=right]:slide-in-from-left-2',
    'data-[side=top]:slide-in-from-bottom-2',
  ),
  {
    variants: {
      contrast: {
        true: 'border-main-border-heavy bg-main-background-contrast text-main-foreground-inverse',
        false: 'border-main-border-default bg-main-background-card text-main-foreground-primary',
      },
      size: {
        default: 'max-w-52',
        large: 'max-w-64',
      },
    },
    defaultVariants: {
      contrast: false,
      size: 'default',
    },
  },
)

type TooltipSize = 'default' | 'large'

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    contrast?: boolean
    size?: TooltipSize
  }
>(({ className, sideOffset = 4, contrast = false, size = 'default', ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(tooltipContentVariants({ contrast, size }), className)}
      data-testid='tooltip-content'
      data-contrast={contrast}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

interface TooltipProps extends TooltipContentProps {
  trigger: React.ReactNode
  tooltipDisabled?: boolean
  triggerClassName?: string
  onOpenChange?: (open: boolean) => void
  open?: boolean
  contrast?: boolean
  asChild?: boolean
  size?: TooltipSize
}

const Tooltip = ({
  className,
  triggerClassName,
  children,
  trigger,
  tooltipDisabled,
  onOpenChange,
  open,
  contrast = false,
  asChild = false,
  size = 'default',
  ...props
}: TooltipProps) => {
  return tooltipDisabled ? (
    <div className={triggerClassName}>{trigger}</div>
  ) : (
    <TooltipProvider>
      <TooltipPrimitive.Root
        onOpenChange={onOpenChange}
        delayDuration={100}
        defaultOpen={open || false}
      >
        <TooltipTrigger className={triggerClassName} asChild={asChild}>
          {trigger}
        </TooltipTrigger>
        {children && (
          <TooltipContent {...props} contrast={contrast} size={size} className={className}>
            {children}
          </TooltipContent>
        )}
      </TooltipPrimitive.Root>
    </TooltipProvider>
  )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
